import { useEffect, useMemo, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Layers3,
  RefreshCw,
  LogOut,
  Users,
  Download,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import * as XLSX from "xlsx";

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

/* ================== TIPAGENS ================== */
type Desafio = {
  id: number;
  nome: string | null;
  nome_fantasia: string | null;
  data_inicio: string | null; // YYYY-MM-DD
  data_fim: string | null; // YYYY-MM-DD
  cor?: string | null;
};

type CheckinRow = {
  id: string;
  created_at: string; // timestamptz
  desafios: {
    id: number;
    nome_fantasia: string | null;
  } | null;
  usuarios: {
    id: number;
    nome: string | null;
    cpf: string | null;
    contato: string | null;
    instagram: string | null;
  } | null;
};

type CheckinQueryRow = {
  id: string;
  created_at: string;
  desafios: { id: number; nome_fantasia: string | null } | null;
  usuarios: {
    id: number;
    nome: string | null;
    cpf: string | null;
    contato: string | null;
    instagram: string | null;
  } | null;
};

type RankingItem = {
  usuario_id: number;
  nome: string;
  cpf: string;
  contato: string;
  instagram: string | null;
  total: number;
};

type UsuarioDesafioRow = {
  desafio_id: number;
  usuario_id: number;
};

/* ================== HELPERS ================== */
function localDayStartISO(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toISOString().split("T")[0] + "T00:00:00.000Z";
}

function localDayEndISO(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
  const localISO = new Date(
    dt.getTime() - dt.getTimezoneOffset() * 60000
  ).toISOString();
  return localISO.split("T")[0] + "T23:59:59.999Z";
}

function todayStr(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function buildBounds(pType: "day" | "range", s: string, e: string) {
  const start = localDayStartISO(s);
  const end = localDayEndISO(pType === "range" ? e : s);
  return { startISO: start, endISO: end };
}

function formatYMDtoBR(ymd: string) {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

// YYYY-MM-DD (local) a partir de Date
function dateToLocalYMD(dt: Date): string {
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Lista todos os dias (YYYY-MM-DD) entre start e end (inclusive)
function enumerateDays(startYMD: string, endYMD: string): string[] {
  const [ys, ms, ds] = startYMD.split("-").map(Number);
  const [ye, me, de] = endYMD.split("-").map(Number);
  const start = new Date(ys, ms - 1, ds);
  const end = new Date(ye, me - 1, de);
  const out: string[] = [];
  const cur = new Date(start);
  while (cur.getTime() <= end.getTime()) {
    out.push(dateToLocalYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function safeFilePart(s: string) {
  if (!s) return "arquivo";
  // remove acentos e caracteres inválidos de filename
  const noAccents = s.normalize?.("NFD").replace(/[\u0300-\u036f]/g, "") ?? s;
  return noAccents
    .replace(/[\\/:*?"<>|]/g, "-") // caracteres proibidos
    .replace(/\s+/g, " ") // colapsa espaços
    .trim()
    .replace(/\s/g, "_"); // troca espaço por _
}

/* ================== COMPONENTE ================== */
export default function AdminOverview() {
  const { toast } = useToast();
  const navigate = useNavigate();

  /* ====== ESTADO APLICADO (queries) ====== */
  const [periodType, setPeriodType] = useState<"day" | "range">("day");
  const [startDate, setStartDate] = useState<string>(todayStr());
  const [endDate, setEndDate] = useState<string>(todayStr());

  /* ====== ESTADO DE EDIÇÃO (UI) ====== */
  const [uiPeriodType, setUiPeriodType] = useState<"day" | "range">("day");
  const [uiStartDate, setUiStartDate] = useState<string>(startDate);
  const [uiEndDate, setUiEndDate] = useState<string>(endDate);

  // Dados base
  const [loading, setLoading] = useState(false);
  const [desafios, setDesafios] = useState<Desafio[]>([]);
  const [checkins, setCheckins] = useState<CheckinRow[]>([]);

  // Ranking: por desafio -> **lista completa** (sem corte) no período
  const [rankingPorDesafio, setRankingPorDesafio] = useState<
    Record<number, RankingItem[]>
  >({});

  // TOTAL REAL de participantes (únicos) por desafio (independente do período)
  const [participantesTotais, setParticipantesTotais] = useState<
    Record<number, number>
  >({});

  // Ranking completo: seleção
  const [desafioSelecionadoRank, setDesafioSelecionadoRank] = useState<
    number | null
  >(null);

  // Filtro do gráfico: "all" ou id do desafio (string)
  const [desafioGrafico, setDesafioGrafico] = useState<string>("all");

  // Limites ISO (estado aplicado)
  const { startISO, endISO } = useMemo(() => {
    const s = startDate;
    const e = periodType === "range" ? endDate : startDate;
    return {
      startISO: localDayStartISO(s),
      endISO: localDayEndISO(e),
    };
  }, [periodType, startDate, endDate]);

  // Label período (estado aplicado)
  const periodoLabel = useMemo(() => {
    if (periodType === "day") {
      return formatYMDtoBR(startDate);
    }
    return `${formatYMDtoBR(startDate)} – ${formatYMDtoBR(
      periodType === "range" ? endDate : startDate
    )}`;
  }, [periodType, startDate, endDate]);

  // Carrega total de participantes (geral)
  const loadParticipantesTotais = async () => {
    try {
      const { data, error } = await supabase
        .from("usuarios_desafios")
        .select("desafio_id, usuario_id");
      if (error) throw error;

      const map = new Map<number, Set<number>>();
      for (const row of (data ?? []) as UsuarioDesafioRow[]) {
        if (!map.has(row.desafio_id))
          map.set(row.desafio_id, new Set<number>());
        map.get(row.desafio_id)!.add(row.usuario_id);
      }
      const out: Record<number, number> = {};
      for (const [did, set] of map.entries()) out[did] = set.size;

      setParticipantesTotais(out);
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao carregar participantes",
        description: "Não foi possível obter o total de participantes.",
        variant: "destructive",
      });
    }
  };

  // Recarregar dados de desafios e check-ins
  const loadData = async (bounds?: { startISO: string; endISO: string }) => {
    try {
      setLoading(true);

      const StartDate = startDate; // comparar apenas a parte de data
      const EndDate = endDate; // idem

      // Desafios ativos pelo período (comparando apenas datas)
      const { data: desafiosData, error: dErr } = await supabase
        .from("desafios")
        .select("id, nome, nome_fantasia, data_inicio, data_fim, cor")
        .lte("data_inicio", StartDate)
        .gte("data_fim", EndDate)
        .order("id", { ascending: true });

      if (dErr) throw dErr;
      setDesafios((desafiosData ?? []) as Desafio[]);

      // Check-ins no intervalo
      const sISO = bounds?.startISO ?? startISO;
      const eISO = bounds?.endISO ?? endISO;

      const { data: rawCheckins, error: cErr } = await supabase
        .from("checkins")
        .select(
          `
            id,
            created_at,
            desafios ( id, nome, nome_fantasia ),
            usuarios ( id, nome, cpf , contato, instagram)
          `
        )
        .gte("created_at", sISO)
        .lte("created_at", eISO)
        .order("created_at", { ascending: true });

      if (cErr) throw cErr;

      const checkinsData = (rawCheckins ?? []) as unknown as CheckinQueryRow[];

      const normalized: CheckinRow[] = checkinsData.map((r) => ({
        id: r.id,
        created_at: r.created_at,
        desafios: r.desafios
          ? {
              id: Number(r.desafios.id),
              nome: r.desafios.nome,
              nome_fantasia: r.desafios.nome_fantasia,
            }
          : null,
        usuarios: r.usuarios
          ? {
              id: Number(r.usuarios.id),
              nome: r.usuarios.nome,
              cpf: r.usuarios.cpf,
              contato: r.usuarios.contato,
              instagram: r.usuarios.instagram ?? null,
            }
          : null,
      }));

      setCheckins(normalized);

      // === Construir ranking **completo** por desafio (no período) ===
      const byDesafio = new Map<number, Map<number, RankingItem>>();

      for (const row of normalized) {
        const did = row.desafios?.id;
        const u = row.usuarios;
        if (!did || !u) continue;

        if (!byDesafio.has(did)) {
          byDesafio.set(did, new Map());
        }
        const mapaUsuarios = byDesafio.get(did)!;

        const curr =
          mapaUsuarios.get(u.id) ??
          ({
            usuario_id: u.id,
            nome: u.nome ?? "Sem nome",
            cpf: u.cpf ?? "",
            contato: u.contato ?? "",
            instagram: u.instagram ?? "",
            total: 0,
          } as RankingItem);

        curr.total += 1;
        mapaUsuarios.set(u.id, curr);
      }

      const finalRanking: Record<number, RankingItem[]> = {};
      for (const [did, mapaUsuarios] of byDesafio.entries()) {
        const arr = Array.from(mapaUsuarios.values()).sort(
          (a, b) => b.total - a.total
        ); // **sem slice** => lista completa
        finalRanking[did] = arr;
      }

      setRankingPorDesafio(finalRanking);

      // define um desafio padrão para a visão "completa"
      if (!desafioSelecionadoRank) {
        const primeiraChave =
          Object.keys(finalRanking).length > 0
            ? Number(Object.keys(finalRanking)[0])
            : null;
        setDesafioSelecionadoRank(primeiraChave);
      }
    } catch (err: unknown) {
      console.error(err);
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: unknown }).message)
          : "Tente novamente mais tarde.";
      toast({
        title: "Erro ao carregar dados",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Atualizar os desafios ativos no período
  const desafiosAtivosNoPeriodo = useMemo(() => {
    return desafios.filter((d) => {
      const ini = d.data_inicio;
      const fim = d.data_fim;
      const iniOK = !ini || ini <= startDate;
      const fimOK = !fim || fim >= endDate;
      return iniOK && fimOK;
    });
  }, [desafios, startDate, endDate]);

  // Atualizar os check-ins por desafio
  const checkinsPorDesafio = useMemo(() => {
    const map = new Map<
      number,
      { id: number; nome: string; nome_fantasia: string; total: number }
    >();

    for (const row of checkins) {
      const did = row.desafios?.id;
      const nome = row.desafios?.nome ?? "Desafio";
      const nome_fantasia = row.desafios?.nome_fantasia ?? "Desafio";
      if (!did) continue;
      const current = map.get(did);
      if (current) current.total += 1;
      else map.set(did, { id: did, nome, nome_fantasia, total: 1 });
    }

    for (const d of desafiosAtivosNoPeriodo) {
      if (!map.has(d.id)) {
        map.set(d.id, {
          id: d.id,
          nome: d.nome ?? `Desafio #${d.id}`,
          nome_fantasia: d.nome_fantasia ?? `Desafio #${d.id}`,
          total: 0,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) =>
      a.nome.localeCompare(b.nome)
    );
  }, [checkins, desafiosAtivosNoPeriodo]);

  // Total de check-ins no período
  const totalCheckinsPeriodo = checkins.length;

  const handleLogout = () => {
    localStorage.removeItem("user_session");
    toast({
      title: "Logout realizado",
      description: "Você foi desconectado com sucesso.",
      variant: "default",
    });
    window.open("/admin", "_self");
  };

  // Aplicar período
  const applyPeriod = async () => {
    if (uiPeriodType === "range" && uiEndDate < uiStartDate) {
      toast({
        title: "Período inválido",
        description: "A data final não pode ser menor que a data inicial.",
        variant: "destructive",
      });
      return;
    }

    const nextType = uiPeriodType;
    const nextStart = uiStartDate;
    const nextEnd = nextType === "range" ? uiEndDate : uiStartDate;

    setPeriodType(nextType);
    setStartDate(nextStart);
    setEndDate(nextEnd);

    const bounds = buildBounds(nextType, nextStart, nextEnd);
    await loadData(bounds);
  };

  // Função para nome do desafio
  const nomeDesafio = (id: number | null) => {
    if (!id) return "—";
    const d = desafios.find((x) => x.id === id);
    return d?.nome ?? `Desafio #${id}`;
  };

  // ===== Série diária de check-ins — multi-desafio =====
  const {
    dadosGrafico,
    chavesDesafio,
    nomePorChave,
  }: {
    dadosGrafico: any[];
    chavesDesafio: string[];
    nomePorChave: Record<string, string>;
  } = useMemo(() => {
    // “dias” base alinhados ao mesmo recorte (ISO helpers)
    const startUtcDay = startISO.split("T")[0];
    const endUtcDay = endISO.split("T")[0];
    const dias = enumerateDays(startUtcDay, endUtcDay);

    const rows: any[] = dias.map((d) => ({
      diaYMD: d,
      dia: d.split("-").reverse().join("/"),
      total: 0,
    }));
    const idxPorDia: Record<string, number> = {};
    dias.forEach((d, i) => (idxPorDia[d] = i));

    // map id->nome (usa ativos no período + presentes nos checkins)
    const idsNomes = new Map<number, string>();
    desafiosAtivosNoPeriodo.forEach((d) =>
      idsNomes.set(d.id, d.nome_fantasia ?? `Desafio #${d.id}`)
    );
    checkins.forEach((r) => {
      if (r.desafios?.id)
        idsNomes.set(
          r.desafios.id,
          r.desafios.nome ?? `Desafio #${r.desafios.id}`
        );
    });

    const nomePorChave: Record<string, string> = {};
    const chaves: string[] = [];
    idsNomes.forEach((nome, id) => {
      const k = `d_${id}`;
      nomePorChave[k] = nome;
      chaves.push(k);
      rows.forEach((r) => (r[k] = 0));
    });

    for (const r of checkins) {
      const did = r.desafios?.id;
      if (!did) continue;
      const bucketUtc = new Date(r.created_at).toISOString().slice(0, 10);
      const i = idxPorDia[bucketUtc];
      if (i === undefined) continue;
      const k = `d_${did}`;
      rows[i][k] += 1;
      rows[i].total += 1;
    }

    return { dadosGrafico: rows, chavesDesafio: chaves, nomePorChave };
  }, [checkins, startISO, endISO, desafiosAtivosNoPeriodo]);

  // ===== Cor por desafio: usa cor do banco; fallback azul =====
  function normalizaCor(c?: string | null) {
    if (!c) return null;
    const s = c.trim();
    if (!s) return null;
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return s;
    if (/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return "#" + s;
    if (/^(rgb|hsl)a?\(/i.test(s)) return s;
    return null;
  }

  const coresPorChave = useMemo<Record<string, string>>(() => {
    const AZUL = "#2563eb";
    const corPorId = new Map<number, string>();
    desafios.forEach((d) => {
      const cor = normalizaCor(d.cor);
      if (cor) corPorId.set(d.id, cor);
    });
    const map: Record<string, string> = {};
    chavesDesafio.forEach((k) => {
      const id = Number(k.slice(2)); // "d_123" -> 123
      map[k] = corPorId.get(id) ?? AZUL;
    });
    return map;
  }, [chavesDesafio, desafios]);

  // ===== Opções do Select do gráfico (usando o que está no gráfico) =====
  const opcoesGrafico = useMemo(
    () =>
      chavesDesafio
        .map((k) => {
          const id = k.slice(2); // "d_123" -> "123"
          return { id, nome: nomePorChave[k] ?? `Desafio #${id}` };
        })
        .sort((a, b) => a.nome.localeCompare(b.nome)),
    [chavesDesafio, nomePorChave]
  );

  useEffect(() => {
    if (
      desafioGrafico !== "all" &&
      !opcoesGrafico.some((o) => o.id === desafioGrafico)
    ) {
      setDesafioGrafico("all");
    }
  }, [opcoesGrafico, desafioGrafico]);

  // ===== Opções do Select do RANKING (agora usando nomePorChave) =====
  const desafiosComRanking = useMemo(() => {
    const ids = Object.keys(rankingPorDesafio).map(Number);
    return ids
      .map((id) => {
        const key = `d_${id}`;
        const nome =
          nomePorChave[key] ??
          desafios.find((d) => d.id === id)?.nome ??
          `Desafio #${id}`;
        const nome_fantasia =
          nomePorChave[key] ??
          desafios.find((d) => d.id === id)?.nome_fantasia ??
          `Desafio #${id}`;
        return { id, nome, nome_fantasia };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [rankingPorDesafio, nomePorChave, desafios]);

  useEffect(() => {
    if (
      desafioSelecionadoRank !== null &&
      !desafiosComRanking.some((o) => o.id === desafioSelecionadoRank)
    ) {
      setDesafioSelecionadoRank(
        desafiosComRanking.length ? desafiosComRanking[0].id : null
      );
    }
  }, [desafiosComRanking, desafioSelecionadoRank]);

  // ===== Exportação Excel (.xlsx) =====
  const exportRankingExcel = (desafioId: number | null) => {
    if (!desafioId) {
      toast({
        title: "Selecione um desafio",
        description:
          "Escolha um desafio para exportar o ranking completo do período.",
        variant: "destructive",
      });
      return;
    }

    // usa o mesmo nome que aparece no gráfico; se não houver, cai no nome do banco
    const key = `d_${desafioId}`;
    const nomeBruto =
      nomePorChave[key] ??
      desafios.find((d) => d.id === desafioId)?.nome ??
      `Desafio ${desafioId}`;

    const rows = rankingPorDesafio[desafioId] ?? [];
    if (rows.length === 0) {
      toast({
        title: "Sem dados para exportar",
        description: "Não há registros no período selecionado.",
      });
      return;
    }

    // monta os dados sem Período / Usuário ID / Desafio
    const dados = rows.map((r, idx) => ({
      "#": idx + 1,
      Nome: r.nome ?? "",
      CPF: r.cpf ?? "",
      Contato: r.contato ?? "",
      "Check-ins": r.total,
      Instagram: r.instagram ?? "",
    }));

    const ws = XLSX.utils.json_to_sheet(dados);
    ws["!cols"] = [
      { wch: 4 }, // #
      { wch: 28 }, // Nome
      { wch: 18 }, // CPF
      { wch: 20 }, // Contato
      { wch: 12 }, // Check-ins
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ranking");

    // nome do arquivo: <Nome do desafio>_<Periodo>.xlsx
    const periodoSafe = safeFilePart(
      periodoLabel.replace(/–/g, "-").replace(/\//g, "-")
    );
    const nomeSafe = safeFilePart(nomeBruto);
    const filename = `${nomeSafe}_${periodoSafe}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  // ===== Efeitos =====
  useEffect(() => {
    loadParticipantesTotais();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-1xl sm:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Painel de Administração
            </h1>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Filtros de período */}
      <Card className="mt-8 mb-6">
        <CardHeader>
          <CardTitle>Período</CardTitle>
          <CardDescription>
            Selecione um dia específico ou um intervalo de dias
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-1">
              <Select
                value={uiPeriodType}
                onValueChange={(v: "day" | "range") => setUiPeriodType(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tipo de período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Dia</SelectItem>
                  <SelectItem value="range">Intervalo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Input
                type="date"
                value={uiStartDate}
                onChange={(e) => {
                  setUiStartDate(e.target.value);
                  if (uiPeriodType === "day") setUiEndDate(e.target.value);
                }}
              />
            </div>

            <div className="md:col-span-2">
              <Input
                type="date"
                value={uiEndDate}
                onChange={(e) => setUiEndDate(e.target.value)}
                disabled={uiPeriodType === "day"}
                min={uiStartDate}
              />
            </div>

            <div className="md:col-span-5 flex gap-3 flex-wrap">
              <Button onClick={applyPeriod} disabled={loading}>
                Aplicar período
              </Button>
              <Button
                onClick={() => loadData()}
                variant="outline"
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                {loading ? "Atualizando..." : "Recarregar"}
              </Button>
              {checkinsPorDesafio.length > 0 && (
                <div className="text-sm text-muted-foreground self-center">
                  Período atual: <strong>{periodoLabel}</strong>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="md:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Desafios ativos no período
            </CardTitle>
            <Layers3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {checkinsPorDesafio.length > 0 && (
              <>
                <div className="text-3xl font-bold text-azul">
                  {checkinsPorDesafio.length}
                </div>
                <CardDescription>Período: {periodoLabel}</CardDescription>
              </>
            )}

            {desafiosAtivosNoPeriodo.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {desafiosAtivosNoPeriodo.map((d) => (
                  <Badge key={d.id} variant="secondary">
                    {d.nome ?? `Desafio #${d.id}`}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Check-ins no período
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          {checkinsPorDesafio.length > 0 && (
            <CardContent>
              <div className="text-3xl font-bold text-success">
                {totalCheckinsPeriodo}
              </div>
              <CardDescription>Registros em {periodoLabel}</CardDescription>
            </CardContent>
          )}
        </Card>

        <Card className="md:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Listar/cadastrar participantes
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/admin/participantes")}
              className="w-full h-10 bg-primary hover:bg-primary/90"
            >
              Ir para Participantes
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico: Evolução diária de check-ins (multi) */}
      <Card className="mb-8">
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <CardTitle>Evolução diária de check-ins</CardTitle>
            <CardDescription>
              Total diário no período selecionado ({periodoLabel})
            </CardDescription>
          </div>

          <div className="w-full md:w-[320px]">
            <Select
              value={desafioGrafico}
              onValueChange={(v) => setDesafioGrafico(v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filtrar gráfico por desafio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os desafios</SelectItem>
                {opcoesGrafico.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent>
          {dadosGrafico.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Sem dados para exibir no período.
            </div>
          ) : (
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={dadosGrafico}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dia" tickMargin={8} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {desafioGrafico === "all"
                    ? chavesDesafio.map((k) => (
                        <Line
                          key={k}
                          type="monotone"
                          dataKey={k}
                          name={nomePorChave[k]}
                          dot={false}
                          strokeWidth={2.2}
                          stroke={coresPorChave[k]} // cor do banco ou azul
                          activeDot={{ r: 4 }}
                        />
                      ))
                    : (() => {
                        const key = `d_${desafioGrafico}`;
                        const nome = nomePorChave[key] ?? "Desafio";

                        return (
                          <Line
                            type="monotone"
                            dataKey={key}
                            name={nome}
                            dot={false}
                            strokeWidth={2.6}
                            stroke={coresPorChave[key]} // cor do banco ou azul
                            activeDot={{ r: 5 }}
                          />
                        );
                      })()}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check-ins por desafio */}
      {checkinsPorDesafio.length > 1 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Check-ins por desafio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Desafio</TableHead>
                    <TableHead className="w-[140px] text-right">
                      Check-ins
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkinsPorDesafio.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.nome}</TableCell>
                      <TableCell className="text-right">
                        <Badge
                          className={
                            d.total > 0 ? "bg-success hover:bg-success/80" : ""
                          }
                        >
                          {d.total}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ======== RANKING COMPLETO + EXPORTAÇÃO ======== */}
      <Card className="mt-8">
        <CardHeader className="gap-4">
          <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
            <div>
              <CardTitle>Ranking completo por desafio</CardTitle>
              <CardDescription>{periodoLabel}</CardDescription>
            </div>

            <div className="flex gap-2 flex-col md:flex-row w-full md:w-auto">
              <Select
                value={
                  desafioSelecionadoRank !== null
                    ? String(desafioSelecionadoRank)
                    : undefined
                }
                onValueChange={(v) => setDesafioSelecionadoRank(Number(v))}
              >
                <SelectTrigger className="min-w-[220px]">
                  <SelectValue placeholder="Selecione um desafio" />
                </SelectTrigger>
                <SelectContent>
                  {desafiosComRanking.length === 0 && (
                    <SelectItem value="0" disabled>
                      Nenhum desafio com dados
                    </SelectItem>
                  )}
                  {desafiosComRanking.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.nome}
                      {console.log("nome", d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={() => exportRankingExcel(desafioSelecionadoRank)}
                variant="outline"
                className="flex items-center gap-2"
                disabled={!desafioSelecionadoRank}
                title="Exportar Excel do ranking completo"
              >
                <Download className="h-4 w-4" />
                Exportar Excel
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {!desafioSelecionadoRank ||
          (rankingPorDesafio[desafioSelecionadoRank] ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Selecione um desafio com dados para visualizar o ranking completo.
            </div>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">#</TableHead>
                    <TableHead>Participante</TableHead>
                    <TableHead>CPF</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>Instagram</TableHead>
                    <TableHead className="text-right">Check-ins</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rankingPorDesafio[desafioSelecionadoRank].map(
                    (item, idx) => (
                      <TableRow key={`${item.usuario_id}-${idx}`}>
                        <TableCell className="font-medium">{idx + 1}</TableCell>
                        <TableCell>{item.nome}</TableCell>
                        <TableCell className="font-mono">{item.cpf}</TableCell>
                        <TableCell className="font-mono">
                          {item.contato}
                        </TableCell>
                        <TableCell className="font-mono">
                          {item.instagram}
                        </TableCell>

                        <TableCell className="text-right">
                          <Badge className="bg-azul text-white">
                            {item.total}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
